import axios from "axios";
import { API_URL } from "configs";

export const USER_MANUAL_PATHS = Object.freeze({
  list: "user-manuals",
  options: "user-manuals/options",
  chunk: "user-manuals/chunk",
  byId: (id) => `user-manuals/${id}`,
  file: (id) => `user-manuals/${id}/file`,
});

export const USER_MANUAL_CHUNK_BYTES = 1 * 1024 * 1024;
const MAX_PDF_BYTES = 10 * 1024 * 1024;
const CHUNK_RETRY_ATTEMPTS = 3;

const authHeaders = (isMultipart = false) => {
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  };
  if (!isMultipart) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
};

const withObject = (objectViewedId, payload = {}) =>
  objectViewedId ? { ...payload, object_viewed_id: objectViewedId } : payload;

export const isPdfFile = (file) => {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  return type === "application/pdf" || name.endsWith(".pdf");
};

export const isAllowedPdfSize = (file) =>
  !!file && Number(file.size || 0) <= MAX_PDF_BYTES;

export const formatFileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!size) return "—";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export const fetchUserManuals = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${USER_MANUAL_PATHS.list}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return {
    data: Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [],
    capabilities: data?.capabilities || {
      can_upload: false,
      can_replace: false,
      can_delete: false,
      can_manage: false,
    },
  };
};

export const fetchUserManualOptions = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${USER_MANUAL_PATHS.options}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data?.data || data || { roles: [] };
};

export const saveUserManual = async (
  payload,
  objectViewedId,
  id = null,
  onProgress = null
) => {
  if (payload.file) {
    return uploadUserManualInChunks(payload, objectViewedId, id, onProgress);
  }

  const formData = new FormData();
  formData.append("object_viewed_id", objectViewedId || "");
  if (payload.title != null) {
    formData.append("title", payload.title);
  }
  const roleIds = Array.isArray(payload.role_ids) ? payload.role_ids : [];
  formData.append("role_ids", JSON.stringify(roleIds));

  const url = id
    ? `${API_URL}${USER_MANUAL_PATHS.byId(id)}`
    : `${API_URL}${USER_MANUAL_PATHS.list}`;

  const { data } = await axios.post(url, formData, {
    headers: authHeaders(true),
  });
  return data?.data || data;
};

const createUploadId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const rand = (Math.random() * 16) | 0;
    const value = char === "x" ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const shouldRetryChunk = (error) => {
  const status = error?.response?.status;
  if (!status) return true;
  if (status === 429) return true;
  return status >= 500;
};

const postUserManualChunk = async (buildFormData, onChunkProgress) => {
  let lastError;
  for (let attempt = 1; attempt <= CHUNK_RETRY_ATTEMPTS; attempt += 1) {
    try {
      const { data } = await axios.post(
        `${API_URL}${USER_MANUAL_PATHS.chunk}`,
        buildFormData(),
        {
          headers: authHeaders(true),
          onUploadProgress: onChunkProgress,
        }
      );
      return data;
    } catch (error) {
      lastError = error;
      if (attempt >= CHUNK_RETRY_ATTEMPTS || !shouldRetryChunk(error)) {
        throw error;
      }
      await delay(400 * attempt);
    }
  }
  throw lastError;
};

const uploadUserManualInChunks = async (
  payload,
  objectViewedId,
  id = null,
  onProgress = null
) => {
  const file = payload.file;
  const totalChunks = Math.ceil(file.size / USER_MANUAL_CHUNK_BYTES);
  const uploadId = createUploadId();
  const reportProgress = (value) => {
    if (typeof onProgress === "function") {
      onProgress(Math.max(0, Math.min(100, Math.round(value))));
    }
  };

  reportProgress(0);
  let result = null;

  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
    const start = chunkIndex * USER_MANUAL_CHUNK_BYTES;
    const end = Math.min(start + USER_MANUAL_CHUNK_BYTES, file.size);
    const blob = file.slice(start, end);

    result = await postUserManualChunk(() => {
      const formData = new FormData();
      formData.append("object_viewed_id", objectViewedId || "");
      formData.append("upload_id", uploadId);
      formData.append("chunk_index", String(chunkIndex));
      formData.append("total_chunks", String(totalChunks));
      formData.append("original_filename", file.name || "user-manual.pdf");
      formData.append("file_size", String(file.size));
      formData.append("mime_type", file.type || "application/pdf");
      formData.append("title", payload.title || "");
      formData.append(
        "role_ids",
        JSON.stringify(Array.isArray(payload.role_ids) ? payload.role_ids : [])
      );
      if (id) {
        formData.append("user_manual_id", id);
      }
      formData.append("file", blob, `chunk-${chunkIndex}.bin`);
      return formData;
    }, (event) => {
      if (!event?.total) return;
      const chunkFraction = event.loaded / event.total;
      reportProgress(((chunkIndex + chunkFraction) / totalChunks) * 100);
    });

    reportProgress(((chunkIndex + 1) / totalChunks) * 100);
  }

  if (result?.status !== "completed") {
    throw new Error("The PDF upload could not be completed. Please try again.");
  }

  reportProgress(100);
  return result?.data || result;
};

export const deleteUserManual = async (id, objectViewedId) => {
  const { data } = await axios.delete(`${API_URL}${USER_MANUAL_PATHS.byId(id)}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data;
};

export const fetchUserManualFileBlob = async (id, objectViewedId, download = false) => {
  const { data } = await axios.get(`${API_URL}${USER_MANUAL_PATHS.file(id)}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      Accept: "application/pdf",
    },
    params: withObject(objectViewedId, download ? { download: 1 } : {}),
    responseType: "blob",
  });
  return data;
};

export const getErrorMessage = (error) => {
  const response = error?.response;
  if (response?.status === 422) {
    const errors = response.data?.errors;
    if (errors && typeof errors === "object") {
      const firstKey = Object.keys(errors)[0];
      const firstMessage = firstKey ? errors[firstKey]?.[0] : null;
      if (firstMessage) return String(firstMessage);
    }
    return response.data?.error || response.data?.message || "Validation failed.";
  }
  return (
    response?.data?.message ||
    response?.data?.error ||
    error?.message ||
    "Something went wrong."
  );
};
